import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { Modal } from '../../../../../web/components/Modal';
import { Button } from '../../../../../web/components-v2/Button';
import { Input } from '../../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../web/components-v2/Select';
import { styleSheet } from './styles';
import {
	BILLING_CYCLE_OPTIONS,
	UpgradeProposalFormData,
	UpgradeProposalType,
	WEBSITE_ADDONS,
	WEBSITE_UPGRADE_OPTIONS,
} from './types';
import { useUpgradeProposalGeneration } from './useUpgradeProposalGeneration';

export interface UpgradeProposalPrefillData {
	websiteUpgrade?: string;
	totalAmountDueToday?: string;
	billingCycle?: string;
	monthsUntilRenewal?: string;
	renewalDate?: string;
	fbAdsChecked?: boolean;
	fbPremiumChecked?: boolean;
	gLSAChecked?: boolean;
}

interface IProps {
	customContentEnabled?: boolean;
	isOpen: boolean;
	onRequestClose: () => void;
	prefillData?: UpgradeProposalPrefillData;
}

export function GenerateUpgradeProposalModal({ customContentEnabled, isOpen, onRequestClose, prefillData }: IProps) {
	const [proposalType, setProposalType] = React.useState<UpgradeProposalType>('standard');

	// Plan and pricing fields
	const [websiteUpgrade, setWebsiteUpgrade] = React.useState('');
	const [totalAmountDueToday, setTotalAmountDueToday] = React.useState('');
	const [billingCycle, setBillingCycle] = React.useState('Annual');
	const [monthsUntilRenewal, setMonthsUntilRenewal] = React.useState('');
	const [renewalDate, setRenewalDate] = React.useState('');
	const [websiteAddon, setWebsiteAddon] = React.useState('None');

	// Custom proposal fields
	const [prospectName, setProspectName] = React.useState('');
	const [prospectTitle, setProspectTitle] = React.useState('');
	const [companyName, setCompanyName] = React.useState('');

	// Add-ons
	const [fbAdsChecked, setFbAdsChecked] = React.useState(false);
	const [fbPremiumChecked, setFbPremiumChecked] = React.useState(false);
	const [gLSAChecked, setGLSAChecked] = React.useState(false);
	const [customContentChecked, setCustomContentChecked] = React.useState(false);
	const [customContentNonprofitChecked, setCustomContentNonprofitChecked] = React.useState(false);

	const prefillAppliedRef = React.useRef(false);

	// Prefill form when modal opens with data from the wizard.
	// The ref ensures prefill only fires once per open — prevents re-applying if prefillData
	// recomputes while the modal is open (e.g. rep changes add-ons in the wizard behind it).
	React.useEffect(() => {
		if (isOpen && prefillData && !prefillAppliedRef.current) {
			prefillAppliedRef.current = true;
			if (prefillData.websiteUpgrade) setWebsiteUpgrade(prefillData.websiteUpgrade);
			if (prefillData.totalAmountDueToday) setTotalAmountDueToday(prefillData.totalAmountDueToday);
			if (prefillData.billingCycle) setBillingCycle(prefillData.billingCycle);
			if (prefillData.monthsUntilRenewal) setMonthsUntilRenewal(prefillData.monthsUntilRenewal);
			if (prefillData.renewalDate) setRenewalDate(prefillData.renewalDate);
			if (prefillData.fbAdsChecked != null) setFbAdsChecked(prefillData.fbAdsChecked);
			if (prefillData.fbPremiumChecked != null) setFbPremiumChecked(prefillData.fbPremiumChecked);
			if (prefillData.gLSAChecked != null) setGLSAChecked(prefillData.gLSAChecked);
		}
		if (!isOpen) {
			prefillAppliedRef.current = false;
		}
	}, [isOpen, prefillData]);

	// Success state
	const [generatedFileId, setGeneratedFileId] = React.useState<string | null>(null);
	const [generatedPdfUrl, setGeneratedPdfUrl] = React.useState<string | null>(null);
	const [generatedFileName, setGeneratedFileName] = React.useState<string | null>(null);

	const proposalMutation = useUpgradeProposalGeneration();

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validation
		if (!websiteUpgrade) {
			toast.error('Please select a website upgrade');
			return;
		}

		if (!totalAmountDueToday || Number.isNaN(Number(totalAmountDueToday))) {
			toast.error('Please enter a valid total amount due today');
			return;
		}

		if (!monthsUntilRenewal || Number.isNaN(Number(monthsUntilRenewal))) {
			toast.error('Please enter months until renewal');
			return;
		}

		if (!renewalDate) {
			toast.error('Please select a renewal date');
			return;
		}

		if (proposalType === 'custom' && !prospectName) {
			toast.error('Please enter prospect name');
			return;
		}

		const formData: UpgradeProposalFormData = {
			billingCycle,
			companyName: proposalType === 'custom' ? companyName : undefined,
			customContentChecked,
			customContentNonprofitChecked,
			fbAdsChecked,
			fbPremiumChecked,
			gLSAChecked,
			monthsUntilRenewal: Number(monthsUntilRenewal),
			proposalType,
			prospectName: proposalType === 'custom' ? prospectName : undefined,
			prospectTitle: proposalType === 'custom' ? prospectTitle : undefined,
			renewalDate,
			totalAmountDueToday: Number(totalAmountDueToday),
			websiteAddon: websiteAddon === 'None' ? '' : websiteAddon,
			websiteUpgrade,
		};

		proposalMutation.mutate(formData, {
			onError: error => {
				toast.error(`Failed to generate upgrade proposal: ${error.message}`);
			},
			onSuccess: data => {
				if (data.success) {
					setGeneratedFileId(data.fileId || null);
					setGeneratedPdfUrl(data.pdfUrl || null);
					setGeneratedFileName(data.fileName || 'Upgrade_Proposal');
					toast.success('Upgrade proposal generated successfully!');
				} else {
					toast.error(`Failed to generate upgrade proposal: ${data.error || 'Unknown error'}`);
				}
			},
		});
	};

	const handleClose = () => {
		// Reset form
		setProposalType('standard');
		setWebsiteUpgrade('');
		setTotalAmountDueToday('');
		setBillingCycle('Annual');
		setMonthsUntilRenewal('');
		setRenewalDate('');
		setWebsiteAddon('None');
		setProspectName('');
		setProspectTitle('');
		setCompanyName('');
		setFbAdsChecked(false);
		setFbPremiumChecked(false);
		setGLSAChecked(false);
		setCustomContentChecked(false);
		setCustomContentNonprofitChecked(false);
		setGeneratedFileId(null);
		setGeneratedPdfUrl(null);
		setGeneratedFileName(null);
		onRequestClose();
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			<div className={css(styleSheet.container)}>
				<h2 className={css(styleSheet.title)}>Generate Upgrade Proposal</h2>

				{!generatedFileId && (
					<>
						{/* Proposal Type Selection */}
						<div className={css(styleSheet.proposalTypeSection)}>
							<label className={css(styleSheet.radioLabel)}>
								<input
									checked={proposalType === 'standard'}
									name='proposalType'
									onChange={() => setProposalType('standard')}
									type='radio'
									value='standard'
								/>
								Standard Proposal
							</label>
							<label className={css(styleSheet.radioLabel)}>
								<input
									checked={proposalType === 'custom'}
									name='proposalType'
									onChange={() => setProposalType('custom')}
									type='radio'
									value='custom'
								/>
								Custom Proposal
							</label>
						</div>

						<form onSubmit={handleSubmit}>
							{/* Plan and Pricing Section */}
							<div className={css(styleSheet.formSection)}>
								<h3 className={css(styleSheet.sectionTitle)}>Website Upgrade & Pricing</h3>

								<div className={css(styleSheet.formGroup)}>
									<label className={css(styleSheet.label)} htmlFor='websiteUpgrade'>
										Select Upgrade *
									</label>
									<Select value={websiteUpgrade} onValueChange={setWebsiteUpgrade}>
										<SelectTrigger styles={styleSheet.select}>
											<SelectValue placeholder='Select Upgrade' />
										</SelectTrigger>
										<SelectContent>
											{WEBSITE_UPGRADE_OPTIONS.map(upgrade => (
												<SelectItem key={upgrade.value} value={upgrade.value}>
													{upgrade.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className={css(styleSheet.formRow)}>
									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='totalAmountDueToday'>
											Total Amount Due Today *
										</label>
										<Input
											styles={[styleSheet.input]}
											id='totalAmountDueToday'
											onChange={e => setTotalAmountDueToday(e.target.value)}
											placeholder='0.00'
											type='number'
											step='0.01'
											min='0'
											value={totalAmountDueToday}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='billingCycle'>
											Billing Cycle *
										</label>
										<Select value={billingCycle} onValueChange={setBillingCycle}>
											<SelectTrigger styles={styleSheet.select}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{BILLING_CYCLE_OPTIONS.map(cycle => (
													<SelectItem key={cycle.value} value={cycle.value}>
														{cycle.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>

								<div className={css(styleSheet.formRow)}>
									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='monthsUntilRenewal'>
											Months Until Renewal *
										</label>
										<Input
											styles={[styleSheet.input]}
											id='monthsUntilRenewal'
											onChange={e => setMonthsUntilRenewal(e.target.value)}
											placeholder='12'
											type='number'
											min='0'
											value={monthsUntilRenewal}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='renewalDate'>
											Renewal Date *
										</label>
										<Input
											styles={[styleSheet.input]}
											id='renewalDate'
											onChange={e => setRenewalDate(e.target.value)}
											type='date'
											value={renewalDate}
										/>
									</div>
								</div>

								{/* Website Add-on (show for Preferred Website) */}
								{websiteUpgrade === 'Preferred Website' && (
									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='websiteAddon'>
											Select Add-on for Preferred Website
										</label>
										<Select value={websiteAddon} onValueChange={setWebsiteAddon}>
											<SelectTrigger styles={styleSheet.select}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{WEBSITE_ADDONS.map(addon => (
													<SelectItem key={addon.value} value={addon.value}>
														{addon.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							</div>

							{/* Custom Proposal Fields */}
							{proposalType === 'custom' && (
								<div className={css(styleSheet.formSection)}>
									<h3 className={css(styleSheet.sectionTitle)}>Prospect Information</h3>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='prospectName'>
											Prospect Name *
										</label>
										<Input
											styles={[styleSheet.input]}
											id='prospectName'
											onChange={e => setProspectName(e.target.value)}
											type='text'
											value={prospectName}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='prospectTitle'>
											Prospect Title
										</label>
										<Input
											styles={[styleSheet.input]}
											id='prospectTitle'
											onChange={e => setProspectTitle(e.target.value)}
											type='text'
											value={prospectTitle}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='companyName'>
											Company Name
										</label>
										<Input
											styles={[styleSheet.input]}
											id='companyName'
											onChange={e => setCompanyName(e.target.value)}
											type='text'
											value={companyName}
										/>
									</div>
								</div>
							)}

							{/* Add-ons Section */}
							<div className={css(styleSheet.formSection)}>
								<h3 className={css(styleSheet.sectionTitle)}>Add-ons</h3>

								<div className={css(styleSheet.addonsGrid)}>
									<Checkbox
										checked={fbPremiumChecked}
										onChange={e => setFbPremiumChecked((e.target as HTMLInputElement).checked)}
									>
										<span className={css(styleSheet.checkboxLabel)}>Meta Ads Premium</span>
									</Checkbox>

									<Checkbox
										checked={gLSAChecked}
										onChange={e => setGLSAChecked((e.target as HTMLInputElement).checked)}
									>
										<span className={css(styleSheet.checkboxLabel)}>Google LSAs</span>
									</Checkbox>

									<Checkbox
										checked={customContentChecked}
										disabled={customContentEnabled}
										onChange={e => setCustomContentChecked((e.target as HTMLInputElement).checked)}
									>
										<span className={css(styleSheet.checkboxLabel)}>
											Custom Content
											{customContentEnabled && ' (Already enabled)'}
										</span>
									</Checkbox>

									<Checkbox
										checked={customContentNonprofitChecked}
										disabled={customContentEnabled}
										onChange={e => setCustomContentNonprofitChecked((e.target as HTMLInputElement).checked)}
									>
										<span className={css(styleSheet.checkboxLabel)}>
											Custom Content (Non-Profit)
											{customContentEnabled && ' (Already enabled)'}
										</span>
									</Checkbox>
								</div>
							</div>

							{/* Action Buttons */}
							<div className={css(styleSheet.buttonRow)}>
								<Button disabled={proposalMutation.isPending} onClick={handleClose} type='button' variant='secondary'>
									Cancel
								</Button>
								<Button disabled={proposalMutation.isPending} isLoading={proposalMutation.isPending} type='submit'>
									Generate Upgrade Proposal
								</Button>
							</div>
						</form>
					</>
				)}

				{/* Success Section */}
				{generatedFileId && (
					<div className={css(styleSheet.successSection)}>
						<h3 className={css(styleSheet.successTitle)}>Upgrade Proposal Generated Successfully!</h3>
						<p className={css(styleSheet.fileName)}>{generatedFileName}.pdf</p>
						<div className={css(styleSheet.downloadButtons)}>
							{generatedPdfUrl && (
								<Button asChild variant='secondary'>
									<a
										download={`${generatedFileName}.pdf`}
										href={generatedPdfUrl}
										rel='noopener noreferrer'
										target='_blank'
									>
										Download PDF
									</a>
								</Button>
							)}
							{generatedFileId && (
								<Button asChild variant='secondary'>
									<a
										href={`https://docs.google.com/presentation/d/${generatedFileId}/edit`}
										rel='noopener noreferrer'
										target='_blank'
									>
										Open in Google Slides
									</a>
								</Button>
							)}
						</div>
						<Button onClick={handleClose} type='button'>
							Close
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
}
